import { injectPstSuffixIntoFilename } from "../utils/pstDateTimeSuffix";

const CSV_CONTENT_TYPE = "text/csv;charset=utf-8;";

const CONTENT_DISPOSITION_HEADER_PATTERN = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i;

const toCsvBlob = (csvContent: Blob | string): Blob =>
  csvContent instanceof Blob ? csvContent : new Blob([csvContent], { type: CSV_CONTENT_TYPE });

/**
 * Decodes a percent-encoded Content-Disposition filename, falling back to the
 * raw value when decoding fails.
 */
const resolveHeaderFileName = (headerFileName: string, fallback: string): string => {
  try {
    return decodeURIComponent(headerFileName).trim() || fallback;
  } catch {
    return headerFileName.trim() || fallback;
  }
};

/**
 * Resolves the CSV file name from the response headers when present, then
 * appends a PST date/time suffix (`_M-D-YYYY_h-m-s`) before the `.csv`
 * extension so every download is uniquely timestamped.
 *
 * @param contentDisposition - Raw Content-Disposition header value.
 * @param fallbackFileName - Default file name to use when the header is absent.
 * @returns The resolved, timestamped file name for the downloaded CSV.
 */
export const resolveCsvFileName = (contentDisposition: string | undefined, fallbackFileName: string): string => {
  const sanitizedFallbackFileName = fallbackFileName.trim();
  const matchedFileName = contentDisposition?.match(CONTENT_DISPOSITION_HEADER_PATTERN);
  const headerFileName = matchedFileName?.[1] ?? matchedFileName?.[2];

  const resolvedName = headerFileName
    ? resolveHeaderFileName(headerFileName, sanitizedFallbackFileName)
    : sanitizedFallbackFileName;

  return injectPstSuffixIntoFilename(resolvedName);
};

/**
 * Decompresses a gzip-encoded Blob using the browser-native DecompressionStream API
 * and returns a plain Blob of the decompressed bytes.
 *
 * This removes the need for the user to manually unzip the downloaded file.
 *
 * **Tradeoffs to be aware of:**
 * - Browser support: requires Chrome 80+, Firefox 113+, Safari 16.4+. Older browsers
 *   will throw; callers should guard with `typeof DecompressionStream !== 'undefined'`.
 * - Memory: both the compressed blob (~10 MB) and the decompressed result (~90 MB) are
 *   held in the tab's heap simultaneously, causing a ~100 MB peak memory spike.
 * - Latency: native C++ implementation adds ~200–600 ms for a 90 MB CSV — negligible
 *   compared to network transfer time, but the UI should keep showing a loading indicator
 *   until the returned Promise resolves.
 * - No external dependencies: intentionally avoids pako/fflate to keep the bundle lean.
 *
 * The function is intentionally pure (Blob in → Promise<Blob> out, no side effects)
 * so it can be unit-tested by injecting synthetic Blobs.
 *
 * @param compressedBlob - The raw gzip Blob returned by the API (responseType: "blob").
 * @returns A Promise that resolves to the decompressed Blob.
 */
export const decompressGzipBlob = async (compressedBlob: Blob): Promise<Blob> => {
  const decompressionStream = new DecompressionStream("gzip");
  const decompressedStream = compressedBlob.stream().pipeThrough(decompressionStream);
  return new Response(decompressedStream).blob();
};

/**
 * Triggers a browser download for CSV content returned by the API.
 * @param csvContent - The CSV content returned by the server.
 * @param fileName - Downloaded file name.
 */
export const downloadCsvFile = (csvContent: Blob | string, fileName: string): void => {
  const blob = toCsvBlob(csvContent);
  const url = globalThis.URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.setAttribute("download", fileName);
  anchor.style.display = "none";

  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  globalThis.URL.revokeObjectURL(url);
};
